import { useEffect, useRef, useState } from 'react';
import { Centrifuge } from 'centrifuge';
import { webSocketUrl } from '@/app/common/api/config';
import { useGenerateTokenMutation } from '@/app/common/api/socketApi';

export type AiAnalysisSocketStatusKey =
  | 'detection_status'
  | 'eeat_status'
  | 'ai_search_status';

type AiAnalysisSocketMessage = {
  data?: {
    client_id?: string;
    data?: {
      detection_status?: string;
      eeat_status?: string;
      ai_search_status?: string;
    };
    detection_status?: string;
    eeat_status?: string;
    ai_search_status?: string;
  };
};

type UseAiDetectionSocketOptions = {
  /** Postgres history row id — channel `personal:#${historyId}` */
  historyId: string | null | undefined;
  active: boolean;
  /** Status fields to watch; defaults to detection only */
  watchStatuses?: AiAnalysisSocketStatusKey[];
  onFinished?: (statusKey: AiAnalysisSocketStatusKey) => void;
};

const DEFAULT_WATCH: AiAnalysisSocketStatusKey[] = ['detection_status'];

const getStatusValue = (
  message: AiAnalysisSocketMessage,
  key: AiAnalysisSocketStatusKey
): string | undefined =>
  message?.data?.data?.[key] ?? message?.data?.[key];

/**
 * Listens for analysis completion on Centrifugo (detection / EEAT).
 * Parent controls lifetime via `active`; does not disconnect on first finish
 * so multiple pending statuses can complete on the same channel.
 */
export function useAiDetectionSocket({
  historyId,
  active,
  watchStatuses = DEFAULT_WATCH,
  onFinished
}: UseAiDetectionSocketOptions) {
  const [token, setToken] = useState<string | null>(null);
  const [generateToken] = useGenerateTokenMutation();
  const handledKeysRef = useRef<Set<string>>(new Set());
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const watchKey = watchStatuses.join('|');

  useEffect(() => {
    if (!active || !historyId) {
      setToken(null);
      handledKeysRef.current = new Set();
      return;
    }

    handledKeysRef.current = new Set();

    const fetchToken = async () => {
      try {
        const response = await generateToken({
          id: historyId,
          channels: [`personal:#${historyId}`]
        }).unwrap();
        setToken(response.data);
      } catch (err) {
        console.error('Error fetching AI analysis socket token:', err);
      }
    };

    void fetchToken();
  }, [active, generateToken, historyId, watchKey]);

  useEffect(() => {
    if (!active || !token || !historyId || watchStatuses.length === 0) return;

    const statuses = watchStatuses;
    const centrifugo = new Centrifuge(webSocketUrl, {
      getToken: async () => token
    });

    centrifugo.on('publication', message => {
      const payload = message as AiAnalysisSocketMessage;
      statuses.forEach(statusKey => {
        const status = getStatusValue(payload, statusKey);
        if (status !== 'finished' || handledKeysRef.current.has(statusKey)) {
          return;
        }
        handledKeysRef.current.add(statusKey);
        onFinishedRef.current?.(statusKey);
      });
    });

    centrifugo.connect();

    return () => {
      centrifugo.disconnect();
    };
    // watchKey captures watchStatuses contents without identity churn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, historyId, token, watchKey]);
}
